import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { Flex, Tabs as PillTab, TabsContainer, CursorPointer } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { AppDispatch, RootState } from 'store'
import {
  getKitchenMenuFeasibility,
  getKitchenMenuPlans,
  getKitchenMenuTemplates,
  instantiateKitchenMenuTemplate,
  publishKitchenMenuPlan,
  upsertKitchenMenuPlan,
  upsertKitchenMenuTemplate,
} from 'features/food/foodSlice'
import {
  FoodMenuPlanStatus,
  IKitchenMenuCycleTemplateResponse,
  IKitchenMenuPlanResponse,
} from 'types/api/foodApi.interface'
import { ISelectValue } from 'constants/globalTypes'
import { formatDateForDisplay } from 'utils/helpers'
import { toast } from 'react-toastify'
import useModalClose from 'hooks/useModalClose'

const formatDay = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const startOfWeek = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  const day = x.getDay()
  const diff = day === 0 ? -6 : 1 - day
  x.setDate(x.getDate() + diff)
  return x
}

const addDays = (d: Date, n: number) => {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

interface IProps {
  onToolbarChange?: (node: React.ReactNode) => void
}

export const MenuPlannerTab = ({ onToolbarChange }: IProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    logisticsKitchens,
    mealWindows,
    dishes,
    menuPlans,
    menuTemplates,
    menuFeasibility,
    isLoading,
  } = useSelector((store: RootState) => store.food)

  const kitchens = logisticsKitchens.data || []
  const windows = mealWindows.data || []
  const dishOptions = (dishes.data || [])
    .filter((d) => d.active)
    .map((d) => ({ label: d.name, value: d.id }))

  const [kitchenId, setKitchenId] = useState<number | null>(null)
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()))
  const [editPlan, setEditPlan] = useState<{
    operationalDay: string
    windowDefId: number
    existing?: IKitchenMenuPlanResponse
  } | null>(null)
  const [entries, setEntries] = useState<{ tagCodes: string[]; dishId: number | null }[]>([])
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [instantiateOpen, setInstantiateOpen] = useState(false)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [templateStart, setTemplateStart] = useState(new Date())
  const [newTemplateName, setNewTemplateName] = useState('')
  const [newTemplateDays, setNewTemplateDays] = useState('7')

  useEffect(() => {
    if (kitchenId == null && kitchens[0]) setKitchenId(kitchens[0].id)
  }, [kitchens, kitchenId])

  const from = formatDay(weekStart)
  const to = formatDay(addDays(weekStart, 6))

  const refresh = () => {
    if (kitchenId == null) return
    dispatch(getKitchenMenuPlans({ from, to, kitchenId }))
  }

  useEffect(() => {
    refresh()
  }, [dispatch, kitchenId, from, to])

  useEffect(() => {
    dispatch(getKitchenMenuTemplates({ page: 1, limit: 50 }))
  }, [dispatch])

  const kitchen = kitchens.find((k) => k.id === kitchenId)
  const tagCodes = (kitchen?.dietaryTagCapabilities || []).map((t) => t.code)

  useEffect(() => {
    onToolbarChange?.(
      <Flex gap='0.5rem' wrap>
        <Button variant='secondary' size='sm' onClick={() => setTemplatesOpen(true)}>
          Templates
        </Button>
        <Button
          variant='secondary'
          size='sm'
          disabled={!menuTemplates.data?.length}
          onClick={() => {
            setTemplateId(menuTemplates.data?.[0]?.id ?? null)
            setInstantiateOpen(true)
          }}
        >
          From template
        </Button>
      </Flex>,
    )
    return () => onToolbarChange?.(null)
  }, [onToolbarChange, menuTemplates.data])

  const openEdit = (operationalDay: string, windowDefId: number) => {
    const existing = (menuPlans || []).find(
      (p) =>
        String(p.operationalDay).slice(0, 10) === operationalDay &&
        p.windowDef?.id === windowDefId &&
        p.kitchen?.id === kitchenId,
    )
    const caps = tagCodes.length ? tagCodes : ['']
    const nextEntries = caps.map((code) => {
      const found = existing?.entries?.find((e) => {
        const codes = (e.dietaryTags || []).map((t) => t.code)
        if (!code) return !codes.length
        return codes.includes(code)
      })
      return { tagCodes: code ? [code] : [], dishId: found?.dish?.id ?? null }
    })
    setEntries(nextEntries.length ? nextEntries : [{ tagCodes: [], dishId: null }])
    setEditPlan({ operationalDay, windowDefId, existing })
    if (existing) dispatch(getKitchenMenuFeasibility(existing.id))
  }

  const savePlan = async () => {
    if (!editPlan || kitchenId == null) return
    const body = {
      operationalDay: editPlan.operationalDay,
      windowDefId: editPlan.windowDefId,
      kitchenId,
      entries: entries
        .filter((e) => e.dishId)
        .map((e) => ({ dietaryTagCodes: e.tagCodes, dishId: Number(e.dishId) })),
    }
    if (!body.entries.length) {
      toast.warn('Select at least one dish')
      return
    }
    const resp = await dispatch(upsertKitchenMenuPlan(body))
    if (resp.meta.requestStatus === 'fulfilled') {
      setEditPlan(null)
      refresh()
    }
  }

  const handlePublish = async (id: number) => {
    const resp = await dispatch(publishKitchenMenuPlan(id))
    if (resp.meta.requestStatus === 'fulfilled') refresh()
  }

  const handleInstantiate = async (dryRun: boolean) => {
    if (!templateId || kitchenId == null) return
    const resp = await dispatch(
      instantiateKitchenMenuTemplate({
        id: templateId,
        body: { kitchenId, startDate: formatDay(templateStart), dryRun },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled' && !dryRun) {
      setInstantiateOpen(false)
      refresh()
    }
  }

  const saveTemplate = async () => {
    if (!newTemplateName.trim() || kitchenId == null) return
    const lengthDays = Number(newTemplateDays) || 7
    const firstWindow = windows[0]
    const firstDish = dishes.data?.[0]
    if (!firstWindow || !firstDish) {
      toast.warn('Need at least one meal window and dish to seed a template')
      return
    }
    const slots = Array.from({ length: lengthDays }, (_, dayIndex) => ({
      dayIndex,
      windowDefId: firstWindow.id,
      dishId: firstDish.id,
      dietaryTagCodes: tagCodes[0] ? [tagCodes[0]] : [],
    }))
    const resp = await dispatch(
      upsertKitchenMenuTemplate({
        name: newTemplateName.trim(),
        lengthDays,
        slots,
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setNewTemplateName('')
      dispatch(getKitchenMenuTemplates({ page: 1, limit: 50 }))
    }
  }

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => formatDay(addDays(weekStart, i))),
    [weekStart],
  )

  const planRows = useMemo(() => {
    const rows: {
      id: string
      day: string
      windowName: string
      windowDefId: number
      plan: IKitchenMenuPlanResponse | null
    }[] = []
    weekDays.forEach((day) => {
      windows.forEach((w) => {
        const plan =
          (menuPlans || []).find(
            (p) =>
              String(p.operationalDay).slice(0, 10) === day &&
              p.windowDef?.id === w.id &&
              p.kitchen?.id === kitchenId,
          ) || null
        rows.push({
          id: `${day}-${w.id}`,
          day,
          windowName: w.name,
          windowDefId: w.id,
          plan,
        })
      })
    })
    return rows
  }, [weekDays, windows, menuPlans, kitchenId])

  const columns: IHonestTableColumn<(typeof planRows)[0]>[] = [
    {
      id: 'day',
      header: 'Day',
      cell: (r) => formatDateForDisplay(r.day),
    },
    { id: 'window', header: 'Window', cell: (r) => r.windowName },
    {
      id: 'status',
      header: 'Status',
      cell: (r) =>
        r.plan ? (
          <StatusPill status={r.plan.status === FoodMenuPlanStatus.Published ? 'success' : 'accent'}>
            {r.plan.status === FoodMenuPlanStatus.Published ? 'Published' : 'Draft'}
          </StatusPill>
        ) : (
          <StatusPill status='neutral'>Empty</StatusPill>
        ),
    },
    {
      id: 'menu',
      header: 'Menu',
      minWidth: '16rem',
      cell: (r) =>
        (r.plan?.entries || [])
          .map(
            (e) =>
              `${(e.dietaryTags || []).map((t) => t.code).join('+') || 'Untagged'}: ${e.dish?.name || '—'}`,
          )
          .join(' · ') || '—',
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <Flex gap='0.5rem' justend>
          <Button size='sm' variant='secondary' onClick={() => openEdit(r.day, r.windowDefId)}>
            {r.plan ? 'Edit' : 'Plan'}
          </Button>
          {r.plan && r.plan.status !== FoodMenuPlanStatus.Published && (
            <Button size='sm' variant='primary' onClick={() => handlePublish(r.plan!.id)}>
              Publish
            </Button>
          )}
        </Flex>
      ),
    },
  ]

  if (!kitchens.length) {
    return <OtherText grey>No kitchens configured yet.</OtherText>
  }

  return (
    <Flex column gap='1rem' width='100%'>
      <Flex gap='0.5rem' center width='100%' between wrap>
        <TabsContainer>
          {kitchens.map((k) => (
            <PillTab key={k.id} active={kitchenId === k.id} onClick={() => setKitchenId(k.id)}>
              {k.name}
            </PillTab>
          ))}
        </TabsContainer>
        <CalendarInput
          nomarg
          labelText='Week starting'
          defaultValue={weekStart}
          onChange={(d: Date) => d && setWeekStart(startOfWeek(d))}
        />
      </Flex>

      <Container>
        <ContainerHeader>
          <TitleText weight='700'>
            Menu plans — {kitchen?.name} · {formatDateForDisplay(from)} – {formatDateForDisplay(to)}
          </TitleText>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={columns}
            data={planRows}
            keyField='id'
            loading={isLoading}
            emptyMessage='No meal windows — configure them under Food → Configuration'
          />
        </ContainerContent>
      </Container>

      {editPlan && (
        <MenuPlanEditModal
          day={editPlan.operationalDay}
          windowName={windows.find((w) => w.id === editPlan.windowDefId)?.name || 'Window'}
          dishOptions={dishOptions}
          entries={entries}
          setEntries={setEntries}
          feasibility={menuFeasibility}
          onClose={() => setEditPlan(null)}
          onSave={savePlan}
        />
      )}

      {templatesOpen && (
        <Modal black width='560px'>
          <Flex column gap='1rem' width='100%' maxHeight='75vh' overflowAuto>
            <Flex between width='100%' center>
              <TitleText weight='bold'>Menu cycle templates</TitleText>
              <CursorPointer onClick={() => setTemplatesOpen(false)}>
                <CloseIcon />
              </CursorPointer>
            </Flex>
            {(menuTemplates.data || []).map((t: IKitchenMenuCycleTemplateResponse) => (
              <OtherText key={t.id} grey>
                #{t.id} {t.name} · {t.lengthDays} days · {(t.slots || []).length} slots
              </OtherText>
            ))}
            {!menuTemplates.data?.length && <OtherText grey>No templates yet</OtherText>}
            <Input
              labelText='New template name'
              placeholder='e.g. Standard 7-day'
              value={newTemplateName}
              onChange={(e: any) => setNewTemplateName(e.target.value)}
              nomarg
            />
            <Input
              labelText='Length (days)'
              type='number'
              placeholder='7'
              value={newTemplateDays}
              onChange={(e: any) => setNewTemplateDays(e.target.value)}
              nomarg
            />
            <OtherText grey>
              Seeds each day with the first meal window and first active dish for this kitchen&apos;s
              first dietary tag — edit slots via API later or re-save after adjusting dishes.
            </OtherText>
            <Button variant='primary' onClick={saveTemplate}>
              Create template
            </Button>
          </Flex>
        </Modal>
      )}

      {instantiateOpen && (
        <Modal black width='480px'>
          <Flex column gap='1rem' width='100%'>
            <Flex between width='100%' center>
              <TitleText weight='bold'>Instantiate template</TitleText>
              <CursorPointer onClick={() => setInstantiateOpen(false)}>
                <CloseIcon />
              </CursorPointer>
            </Flex>
            <HonestSelectInput
              nomarg
              labelText='Template'
              placeholder='Select template…'
              options={(menuTemplates.data || []).map((t) => ({
                label: `${t.name} (${t.lengthDays}d)`,
                value: t.id,
              }))}
              defaultValue={templateId || undefined}
              onChange={(v: ISelectValue) => setTemplateId(Number(v.value))}
              name='template'
            />
            <CalendarInput
              nomarg
              labelText='Start date'
              defaultValue={templateStart}
              onChange={(d: Date) => d && setTemplateStart(d)}
            />
            <Flex gap='0.5rem' justcenter>
              <Button variant='secondary' onClick={() => handleInstantiate(true)}>
                Dry run
              </Button>
              <Button variant='primary' onClick={() => handleInstantiate(false)}>
                Apply
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </Flex>
  )
}

const MenuPlanEditModal = ({
  day,
  windowName,
  dishOptions,
  entries,
  setEntries,
  feasibility,
  onClose,
  onSave,
}: {
  day: string
  windowName: string
  dishOptions: { label: string; value: number }[]
  entries: { tagCodes: string[]; dishId: number | null }[]
  setEntries: (v: { tagCodes: string[]; dishId: number | null }[]) => void
  feasibility: RootState['food']['menuFeasibility']
  onClose: () => void
  onSave: () => void
}) => {
  useModalClose(onClose)

  return (
    <Modal black width='560px'>
      <Flex column gap='1rem' width='100%' maxHeight='75vh' overflowAuto>
        <Flex between width='100%' center>
          <TitleText weight='bold'>
            Plan menu — {formatDateForDisplay(day)} · {windowName}
          </TitleText>
          <CursorPointer onClick={onClose}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        {entries.map((e, i) => (
          <HonestSelectInput
            key={i}
            nomarg
            width='100%'
            labelText={e.tagCodes.length ? e.tagCodes.join(' + ') : 'Untagged'}
            placeholder='Select dish…'
            options={dishOptions}
            defaultValue={e.dishId || undefined}
            onChange={(v: ISelectValue) =>
              setEntries(
                entries.map((x, j) => (j === i ? { ...x, dishId: Number(v.value) || null } : x)),
              )
            }
            name={`dish-${i}`}
          />
        ))}
        {feasibility && (
          <Flex column gap='0.5rem'>
            <BodyText weight='600'>
              Feasibility — {feasibility.plannedMeals} planned meals
              {feasibility.demandIsFirm ? ' (firm)' : ' (forecast)'}
              {feasibility.hasShortfall ? ' · SHORTFALL' : ''}
            </BodyText>
            <OtherText grey>Est. cost: {feasibility.estimatedCost}</OtherText>
            {(feasibility.lines || []).map((l, idx) => (
              <OtherText key={idx} grey>
                {l.ingredient?.name || '—'}: need {l.required}, have {l.available}
                {l.shortfall > 0 ? ` · short ${l.shortfall}` : ''}
              </OtherText>
            ))}
          </Flex>
        )}
        <Flex gap='0.5rem' justcenter>
          <Button variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button variant='primary' onClick={onSave}>
            Save draft
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
